import { Input } from '@rebass/forms'
import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChangeOptions
} from 'downshift'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Flex, Text } from 'rebass'
import { stripSsml, UneeqContext, useUneeqState } from 'uneeq-react-core'
import { useSendSocketMessage } from '../../../app/hooks/useSendSocketMessage'
import { useTvContext } from '../../../app/hooks/useTvContext'
import { ReactComponent as ExitIcon } from '../exit.svg'
import { markdownQuestion } from '../Question'
import styles from '../styles'

function stateReducer(
  state: UseComboboxState<any>,
  actionAndChanges: UseComboboxStateChangeOptions<any>
) {
  const { type, changes } = actionAndChanges
  switch (type) {
    case useCombobox.stateChangeTypes.InputChange:
      return {
        ...changes,
        selectedItem: null
      }
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
      return {
        ...changes,
        selectedItem: null,
        inputValue: ''
      }

    default:
      return changes
  }
}

const AutocompleteQuestion = () => {
  const {
    mayaQuestion,
    autocompleteOptions,
    hideQuestionTitle
  } = useUneeqState()
  const { dispatch } = useContext(UneeqContext)
  const { t } = useTranslation()
  const sendMessage = useSendSocketMessage()
  const questionText = useMemo(
    () => markdownQuestion(stripSsml(mayaQuestion.question)),
    [mayaQuestion]
  )
  const [inputItems, setInputItems] = useState(autocompleteOptions || [])
  const { tvAppMode } = useTvContext()

  const submitOption = (selectedItem?: { id: string; label: string }) => {
    const info = {
      type: 'response',
      questionId: mayaQuestion.id,
      response: selectedItem?.id ? selectedItem.id.toString() : '',
      label: selectedItem?.label
        ? selectedItem.label
        : t('Transcript.skippedQuestion')
    }
    dispatch({ type: 'mayaMessage', payload: info })
    sendMessage(info)
  }

  useEffect(() => {
    autocompleteOptions && setInputItems(autocompleteOptions)
  }, [autocompleteOptions])

  const {
    isOpen,
    selectedItem,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    reset
  } = useCombobox({
    items: inputItems,
    stateReducer,
    itemToString: (item: any) => item.label,
    onInputValueChange: ({ inputValue }) => {
      sendMessage({
        type: 'autocomplete',
        questionId: mayaQuestion.id,
        input: inputValue
      })
    },
    onSelectedItemChange: ({ selectedItem }) => {
      // if (!tvAppMode && selectedItem && mayaQuestion.autoSubmit) {
      if (selectedItem && mayaQuestion.autoSubmit) {
        submitOption(selectedItem)
      }
    }
  })

  const showSkipButton = mayaQuestion.optional && !selectedItem

  return (
    <Flex sx={styles.questionContainer}>
      <Flex sx={styles.topContainer}>
        {!hideQuestionTitle && <Text sx={styles.question}>{questionText}</Text>}
        <Text sx={styles.instructions}>{mayaQuestion.instruction}</Text>
      </Flex>
      <Flex sx={styles.autocompleteContainer}>
        <ul {...getMenuProps()} style={styles.autocompleteMenu}>
          {inputItems.length > 0 &&
            inputItems.map((item: any, index: any) => (
              <Box
                as="li"
                className="focusable"
                key={`${item.label}${index}`}
                variant="buttons.select"
                {...getItemProps({ item, index })}
                sx={styles.autocompleteItem(selectedItem?.id === item.id)}
              >
                {item.label}
              </Box>
            ))}
        </ul>
        <Flex
          {...getComboboxProps()}
          sx={{ ...styles.autocompleteCombobox, width: '100%' }}
        >
          <Flex sx={{ ...styles.autocompleteInputContainer }}>
            <Input
              {...getInputProps()}
              placeholder={
                mayaQuestion.placeholder ||
                t('Question.autoCompletePlaceholder')
              }
              autoFocus
              sx={{
                ...styles.textInput
              }}
            />
            <Box
              id="clear"
              onClick={reset}
              sx={styles.autocompleteInputClearButton}
            >
              <ExitIcon />
            </Box>
          </Flex>
          {!mayaQuestion.autoSubmit && !showSkipButton && (
            <Button
              disabled={!selectedItem}
              type="submit"
              onClick={() => submitOption(selectedItem)}
              sx={styles.button}
            >
              {t('Question.submit')}
            </Button>
          )}
          {showSkipButton && (
            <Button
              type="submit"
              onClick={() => submitOption()}
              sx={styles.button}
            >
              {t('Question.skip')}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default AutocompleteQuestion
